// Listing quick-view — full-information redesign (Haim runtime round 3: "show
// EVERYTHING we know about the listing, look at the listingsMatch card but do
// better"). Still pure presentation over data already in memory — zero fetches, so
// it opens instantly. Field inventory + labels ported from the legacy
// listingsMatch/ListingsMatchRow.tsx, ListingPropertiesAndAdditionalDetails.tsx
// (amenity/tile icon map) and ListingPriceWithHistory.tsx; UI built fresh:
//  - price with the clickable price-history chip (shared ListingPriceWithHistory);
//  - pastel quality chips: property type (blue), condition (green), בלעדי (red + 💎),
//    ad type, rent/commercial markers, furniture, tags;
//  - de-condensed key details: icon+value tiles (rooms/floor/sqm/plot/garden/terrace/
//    parking/entrance) instead of the crammed dot-separated line;
//  - amenity chips with icons for every true boolean (מעלית, ממ״ד, ...);
//  - honest dates: פורסם לראשונה / עודכן במקור / נסרק לאחרונה (scraped origins) or
//    עודכן לאחרונה (webot), relative + absolute;
//  - seller section: name, agency, phone via the shared PhoneNumberDisplay, original
//    listing + video links. No phone on the doc → honest "not available" caption (the
//    frontend has NO seller-phone fetch endpoint; phones arrive via server backfill).
//  - gallery + watermark/hidden-picture logic, override-aware description and realtor
//    note kept intact from the previous version. Full-screen on phones.
import { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import "moment/locale/he"; // without this line, the Hebrew relative times are not imported
import {
  Box,
  Stack,
  Button,
  Dialog,
  Divider,
  Tooltip,
  IconButton,
  Typography,
  useMediaQuery,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import {
  Spa,
  Home,
  Fence,
  AcUnit,
  Shield,
  Close,
  Balcony,
  History,
  Security,
  Elevator,
  DoorBack,
  OpenInNew,
  Accessible,
  ViewAgenda,
  Foundation,
  FormatPaint,
  ChevronLeft,
  ChevronRight,
  LocalParking,
  VisibilityOff,
  EventAvailable,
  GridViewOutlined,
  PlayCircleOutline,
  ApartmentOutlined,
  MeetingRoomOutlined
} from "@material-ui/icons";
import Label from "components/Label";
import SvgIconStyle from "components/SvgIconStyle";
import createAvatar from "utils/createAvatar";
import { MAvatar } from "components/@material-extend";
import PhoneNumberDisplay from "components/PhoneNumberDisplay";
import { genericPropertyDetailsKeys } from "../../../../../../@types/sharedSchema";
import type {
  GenericPropertyDetails,
  FavoriteListingsOfContact
} from "../../../../../../@types/sharedSchema";
import { getListingPicturesVisibility } from "components/_dashboard/listingsMatch/ListingPicturesVisibilty";
import type { MatchHit } from "./matchesSearch";
import {
  coerceDate,
  AMENITY_LABELS,
  formatShortDate,
  buildListingAddressLine
} from "./matchesDisplay";
import ListingPriceWithHistory from "./ListingPriceWithHistory";
import type { ReactNode, ComponentType } from "react";

const ORIGIN_TAGS: Record<string, string> = { yad2: "יד2", madlan: "מדלן", webot: "נכס שלי" };

const AD_TYPE_LABELS: Record<string, string> = { private: "פרטי", commercial: "תיווך" };

// Amenity icon map — ported from listingsMatch/ListingPropertiesAndAdditionalDetails.tsx
const AMENITY_ICONS: Record<keyof GenericPropertyDetails, ComponentType<{ sx?: object }>> = {
  hasBars: Fence,
  hasGarden: Spa,
  hasParking: LocalParking,
  hasStorage: DoorBack,
  hasTerrace: Balcony,
  isRenovated: FormatPaint,
  hasElevator: Elevator,
  hasProtectedSpace: Security,
  hasAirConditioning: AcUnit,
  hasHandicappedAccess: Accessible,
  hasHousingUnit: Home,
  hasMamak: Shield,
  hasMiklat: Foundation
};

type ChipPalette = "primary" | "secondary" | "info" | "success" | "warning" | "error" | "grey";

/** Pastel quality chip — Haim's screenshot style: soft fill, soft border, tiny icon. */
function QualityChip({
  label,
  palette,
  icon
}: {
  label: string;
  palette: ChipPalette;
  icon?: ReactNode;
}): JSX.Element {
  const theme = useTheme();
  const main = palette === "grey" ? theme.palette.grey[500] : theme.palette[palette].main;
  const text = palette === "grey" ? theme.palette.text.secondary : theme.palette[palette].dark;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: 999,
        typography: "caption",
        fontWeight: 600,
        color: text,
        bgcolor: alpha(main, 0.1),
        border: `1px solid ${alpha(main, 0.28)}`
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

/** Icon + value tile — compact inline pill: icon sits beside the value/label text
 * (not stacked above it), tight padding, content-sized so many fit per row. */
function DetailTile({
  icon,
  value,
  label
}: {
  icon: ReactNode;
  value: string | number;
  label: string;
}): JSX.Element {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.grey[500], 0.08)
      }}
    >
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.2, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

/** "פורסם לראשונה לפני 3 שבועות · 1.7.2026" — relative + absolute, honest labels. */
function DateLine({ label, date }: { label: string; date: Date }): JSX.Element {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <History sx={{ fontSize: 14, color: "text.disabled" }} />
      <Typography variant="caption" color="text.secondary" noWrap>
        {label} {moment(date).locale("he").fromNow()} · {formatShortDate(date)}
      </Typography>
    </Stack>
  );
}

type ListingQuickViewDialogProps = {
  open: boolean;
  listing: MatchHit | null;
  /** Minisite context (nullable): makes the description override-aware and marks hidden photos. */
  favorites?: FavoriteListingsOfContact | null;
  onClose: () => void;
  /** Footer actions — composed by the caller (WhatsApp / edit / add / remove). */
  actions?: ReactNode;
};

export default function ListingQuickViewDialog({
  open,
  listing,
  favorites,
  onClose,
  actions
}: ListingQuickViewDialogProps): JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [imageIndex, setImageIndex] = useState(0);

  const listingId = listing?.objectID ?? "";
  useEffect(() => {
    setImageIndex(0);
  }, [listingId, open]);

  const images = useMemo(() => {
    if (!listing) return [] as string[];
    const urls = listing.imageUrls?.length
      ? listing.imageUrls
      : [listing.coverImageUrl].filter((u): u is string => Boolean(u));
    return urls;
  }, [listing]);

  if (!listing) return <></>;

  const inMinisite = Boolean(favorites?.listingIds?.includes(listingId));
  const details = favorites?.additionalDetails?.[listingId];
  const visibilityMode =
    inMinisite && favorites ? getListingPicturesVisibility(listingId, favorites) : "all";
  const hiddenPictures = details?.hiddenPictures?.pictures ?? [];
  const isImageHiddenFromCustomer = (src: string): boolean =>
    visibilityMode === "none" || (visibilityMode === "some" && hiddenPictures.includes(src));

  const descriptionOverride = details?.descriptionOverride?.trim();
  const description = descriptionOverride || listing.description?.trim() || "";
  const addressLine = buildListingAddressLine(listing);
  const areaLine = [listing.address?.neighborhood, listing.address?.city]
    .filter(Boolean)
    .join(", ");
  const currentImage = images[imageIndex];

  // ── quality chips (screenshot semantics: type=blue, condition=green, בלעדי=red+💎) ──
  const qualityChips: { key: string; label: string; palette: ChipPalette; icon?: ReactNode }[] =
    [];
  if (listing.propertyType) {
    qualityChips.push({
      key: "propertyType",
      label: listing.propertyType,
      palette: "info",
      icon: <Home sx={{ fontSize: 14 }} />
    });
  }
  if (listing.propertyCondition) {
    qualityChips.push({
      key: "propertyCondition",
      label: listing.propertyCondition,
      palette: "success",
      icon: <FormatPaint sx={{ fontSize: 14 }} />
    });
  }
  if (listing.isAssetExclusive) {
    qualityChips.push({
      key: "exclusive",
      label: "בלעדי",
      palette: "error",
      icon: <SvgIconStyle src="/static/icons/diamond.svg" sx={{ width: 13, height: 13 }} />
    });
  }
  if (listing.listingListedFor === "rent") {
    qualityChips.push({ key: "rent", label: "להשכרה", palette: "warning" });
  }
  if (listing.purposeType === "commercial") {
    qualityChips.push({ key: "purpose", label: "מסחרי", palette: "secondary" });
  }
  if (listing.adType && AD_TYPE_LABELS[listing.adType]) {
    qualityChips.push({ key: "adType", label: AD_TYPE_LABELS[listing.adType], palette: "grey" });
  }
  if (listing.includesFurniture) {
    qualityChips.push({ key: "furniture", label: "כולל ריהוט", palette: "warning" });
  }
  (listing.tags ?? []).forEach((tag) =>
    qualityChips.push({ key: `tag-${tag}`, label: tag, palette: "warning" })
  );

  // ── key detail tiles (de-condensed meta line) ─────────────────────────────────
  const floor = listing.address?.floor;
  const entranceValue = (() => {
    if (listing.isImmediateEntrance || listing.entranceType === "immediate") return "מיידית";
    if (listing.isEnterDateFlexible || listing.entranceType === "flexible") return "גמישה";
    const entranceDate = coerceDate(listing.entranceDate as unknown as Date | number | string);
    return entranceDate ? formatShortDate(entranceDate) : null;
  })();

  const tiles: { key: string; icon: ReactNode; value: string | number; label: string }[] = [];
  if (typeof listing.roomsCount === "number" && listing.roomsCount > 0) {
    tiles.push({
      key: "rooms",
      icon: <MeetingRoomOutlined sx={{ fontSize: 16 }} />,
      value: listing.roomsCount,
      label: "חדרים"
    });
  }
  if (typeof floor === "number") {
    tiles.push({
      key: "floor",
      icon: <ApartmentOutlined sx={{ fontSize: 16 }} />,
      value: floor === 0 ? "קרקע" : floor,
      label: listing.buildingTotalFloors ? `קומה (מתוך ${listing.buildingTotalFloors})` : "קומה"
    });
  }
  if (typeof listing.squareMeter === "number" && listing.squareMeter > 0) {
    tiles.push({
      key: "sqm",
      icon: <GridViewOutlined sx={{ fontSize: 16 }} />,
      value: listing.squareMeter,
      label: "מ״ר"
    });
  }
  if (typeof listing.sizeOfPlotM2 === "number" && listing.sizeOfPlotM2 > 0) {
    tiles.push({
      key: "plot",
      icon: <ViewAgenda sx={{ fontSize: 16 }} />,
      value: listing.sizeOfPlotM2,
      label: "מ״ר מגרש"
    });
  }
  if (typeof listing.sizeOfGardenM2 === "number" && listing.sizeOfGardenM2 > 0) {
    tiles.push({
      key: "garden",
      icon: <Spa sx={{ fontSize: 16 }} />,
      value: listing.sizeOfGardenM2,
      label: "מ״ר גינה"
    });
  }
  if (typeof listing.sizeOfTerraceM2 === "number" && listing.sizeOfTerraceM2 > 0) {
    tiles.push({
      key: "terrace",
      icon: <Balcony sx={{ fontSize: 16 }} />,
      value: listing.sizeOfTerraceM2,
      label: "מ״ר מרפסת"
    });
  }
  if (typeof listing.parkingSpotsCount === "number" && listing.parkingSpotsCount > 0) {
    tiles.push({
      key: "parking",
      icon: <LocalParking sx={{ fontSize: 16 }} />,
      value: listing.parkingSpotsCount,
      label: "חניות"
    });
  }
  if (entranceValue) {
    tiles.push({
      key: "entrance",
      icon: <EventAvailable sx={{ fontSize: 16 }} />,
      value: entranceValue,
      label: "כניסה"
    });
  }

  const amenities = genericPropertyDetailsKeys.filter(
    (key) => (listing as unknown as Record<string, unknown>)[key] === true
  );

  // ── honest dates ──────────────────────────────────────────────────────────────
  const isWebotListing = listing.listingOrigin === "webot";
  const publishedAt = coerceDate(listing.listingPublishedTime ?? listing.createdTime);
  const sourceUpdatedAt = coerceDate(listing.listingUpdatedTime);
  const scannedAt = coerceDate(listing.updatedTime);

  // Staleness warning — ported from legacy ListingsMatchRow (7d yad2 / 3d madlan): a
  // scraped listing our scraper hasn't re-confirmed recently may be gone from the source.
  const staleAfterDays =
    listing.listingOrigin === "yad2" ? 7 : listing.listingOrigin === "madlan" ? 3 : null;
  const isPotentiallyRemoved =
    staleAfterDays !== null &&
    scannedAt !== null &&
    Date.now() - scannedAt.getTime() > staleAfterDays * 24 * 60 * 60 * 1000;
  const dateLines: { key: string; label: string; date: Date }[] = [];
  if (publishedAt) dateLines.push({ key: "published", label: "פורסם לראשונה", date: publishedAt });
  if (isWebotListing) {
    const webotUpdatedAt = sourceUpdatedAt ?? scannedAt;
    if (webotUpdatedAt && webotUpdatedAt.getTime() !== publishedAt?.getTime()) {
      dateLines.push({ key: "updated", label: "עודכן לאחרונה", date: webotUpdatedAt });
    }
  } else {
    if (sourceUpdatedAt) dateLines.push({ key: "updated", label: "עודכן במקור", date: sourceUpdatedAt });
    if (scannedAt) dateLines.push({ key: "scanned", label: "נסרק לאחרונה", date: scannedAt });
  }

  // ── seller ────────────────────────────────────────────────────────────────────
  const sellerName =
    listing.sellerName && listing.sellerName !== "N/A" ? listing.sellerName.trim() : "";
  const agencyName = listing.agencyName?.trim() ?? "";
  const sellerPhone = listing.sellerPhoneNumber ?? "";
  const originalUrl = listing.originalListingUrl ?? "";
  const videoUrl = listing.videoUrl ?? "";
  const hasSellerSection = Boolean(sellerName || agencyName || sellerPhone || originalUrl || videoUrl);
  const sellerAvatar = sellerName ? createAvatar(sellerName) : null;

  const realtorNote = details?.listingNotes?.trim();
  const locationDetails = listing.locationDetails?.trim();

  const step = (delta: number): void => {
    if (images.length === 0) return;
    setImageIndex((prev) => (prev + delta + images.length) % images.length);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogContent sx={{ p: 0 }}>
        {/* gallery — watermark-effective images are passed in by the caller; hidden-from-
            customer marks derive from the favorites doc (both unchanged from v1) */}
        <Box
          sx={{
            position: "relative",
            height: { xs: 220, sm: 320 },
            bgcolor: "grey.200",
            backgroundImage: currentImage
              ? `url(${currentImage})`
              : "linear-gradient(135deg, #dfe3e8 0%, #c4cdd5 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              insetInlineEnd: 8,
              color: "common.white",
              bgcolor: alpha(theme.palette.grey[900], 0.5),
              "&:hover": { bgcolor: alpha(theme.palette.grey[900], 0.7) }
            }}
          >
            <Close fontSize="small" />
          </IconButton>

          {ORIGIN_TAGS[listing.listingOrigin] && (
            <Box sx={{ position: "absolute", top: 8, insetInlineStart: 8 }}>
              <Label color="default" variant="filled">
                {ORIGIN_TAGS[listing.listingOrigin]}
              </Label>
            </Box>
          )}

          {currentImage && isImageHiddenFromCustomer(currentImage) && (
            <Box sx={{ position: "absolute", bottom: 8, insetInlineStart: 8 }}>
              <Label color="warning" variant="filled">
                <VisibilityOff sx={{ fontSize: 14, ml: 0.5 }} /> מוסתרת מהלקוח
              </Label>
            </Box>
          )}

          {/* prev/next — reasoned in LOGICAL terms so RTL and LTR both work:
              the inline-END edge is "forward in reading order" → NEXT (physical right
              in LTR, physical left in RTL); inline-START → PREVIOUS. insetInline* is
              natively direction-aware (and untouched by stylis-rtl, which flips physical
              left/right CSS but NOT icon glyphs or JS handlers — the original bug). The
              chevron glyph is physical, so it's picked per direction to point OUTWARD at
              the edge the button lands on: under RTL the physically-right button shows
              ChevronRight and goes to the PREVIOUS image, the physically-left button
              shows ChevronLeft and goes NEXT; under LTR right=next, left=previous. */}
          {images.length > 1 && (
            <>
              <IconButton
                aria-label="התמונה הבאה"
                onClick={() => step(1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  insetInlineEnd: 8,
                  transform: "translateY(-50%)",
                  color: "common.white",
                  bgcolor: alpha(theme.palette.grey[900], 0.4),
                  "&:hover": { bgcolor: alpha(theme.palette.grey[900], 0.6) }
                }}
              >
                {theme.direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
              <IconButton
                aria-label="התמונה הקודמת"
                onClick={() => step(-1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  insetInlineStart: 8,
                  transform: "translateY(-50%)",
                  color: "common.white",
                  bgcolor: alpha(theme.palette.grey[900], 0.4),
                  "&:hover": { bgcolor: alpha(theme.palette.grey[900], 0.6) }
                }}
              >
                {theme.direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </>
          )}
        </Box>

        {/* thumbnail strip */}
        {images.length > 1 && (
          <Stack direction="row" spacing={0.75} sx={{ px: 2, pt: 1.25, overflowX: "auto" }}>
            {images.map((src, i) => (
              <Box
                key={src}
                onClick={() => setImageIndex(i)}
                sx={{
                  flex: "0 0 auto",
                  width: 56,
                  height: 42,
                  borderRadius: 0.75,
                  cursor: "pointer",
                  backgroundImage: `url(${src})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  outline:
                    i === imageIndex
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                  opacity: isImageHiddenFromCustomer(src) ? 0.35 : 1
                }}
              />
            ))}
          </Stack>
        )}

        {/* details */}
        <Stack spacing={1.75} sx={{ p: { xs: 2, sm: 2.5 } }}>
          {/* price (with history) + address */}
          <Stack spacing={0.25}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25}>
              <ListingPriceWithHistory
                price={listing.price}
                priceHistory={listing.priceHistory}
                lastPriceChangeDate={listing.lastPriceChangeDate}
                variant="h5"
              />
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap title={addressLine}>
                  {addressLine}
                </Typography>
                {areaLine && areaLine !== addressLine && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {areaLine}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>

          {isPotentiallyRemoved && (
            <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600 }}>
              ⚠️ לא נסרק כבר {moment(scannedAt as Date).locale("he").fromNow(true)} — ייתכן
              שהמודעה כבר לא פעילה במקור
            </Typography>
          )}

          {/* quality chips */}
          {qualityChips.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {qualityChips.map(({ key, label, palette, icon }) => (
                <QualityChip key={key} label={label} palette={palette} icon={icon} />
              ))}
            </Stack>
          )}

          {/* key detail tiles — compact wrap row, tiles size to content */}
          {tiles.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {tiles.map(({ key, icon, value, label }) => (
                <DetailTile key={key} icon={icon} value={value} label={label} />
              ))}
            </Stack>
          )}

          {/* description — directly under the stat tiles; dir="auto" + textAlign start so
              Hebrew text aligns right and Latin text aligns left per the CONTENT language */}
          {description && (
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2">תיאור</Typography>
                {descriptionOverride && <Label color="info">תיאור מותאם ללקוח</Label>}
              </Stack>
              <Typography
                variant="body2"
                dir="auto"
                sx={{ whiteSpace: "pre-wrap", textAlign: "start" }}
              >
                {description}
              </Typography>
            </Stack>
          )}

          {/* amenities */}
          {amenities.length > 0 && (
            <Stack spacing={0.75}>
              <Typography variant="subtitle2">יש בנכס</Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {amenities.map((key) => {
                  const AmenityIcon = AMENITY_ICONS[key];
                  return (
                    <Box
                      key={key}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.4,
                        borderRadius: 999,
                        typography: "caption",
                        color: "text.secondary",
                        bgcolor: alpha(theme.palette.grey[500], 0.1),
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`
                      }}
                    >
                      {AmenityIcon && <AmenityIcon sx={{ fontSize: 14 }} />}
                      {AMENITY_LABELS[key] ?? key}
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          )}

          {/* dates — relative + absolute, honest per origin */}
          {dateLines.length > 0 && (
            <Stack direction="row" flexWrap="wrap" columnGap={2.5} rowGap={0.5}>
              {dateLines.map(({ key, label, date }) => (
                <DateLine key={key} label={label} date={date} />
              ))}
            </Stack>
          )}

          {/* seller */}
          {hasSellerSection && (
            <>
              <Divider sx={{ borderStyle: "dashed" }} />
              <Stack spacing={0.75}>
                <Typography variant="subtitle2">המפרסם</Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={1.5}
                  sx={{ minWidth: 0 }}
                >
                  {sellerAvatar && (
                    <MAvatar sx={{ width: 40, height: 40 }} color={sellerAvatar.color}>
                      {sellerAvatar.name}
                    </MAvatar>
                  )}
                  <Stack sx={{ minWidth: 0, flex: 1 }}>
                    {sellerName && (
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {sellerName}
                      </Typography>
                    )}
                    {agencyName && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {agencyName}
                      </Typography>
                    )}
                    {sellerPhone ? (
                      <PhoneNumberDisplay phone={sellerPhone} variant="full" />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        טלפון המפרסם אינו זמין
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    {videoUrl && (
                      <Tooltip title="סרטון הנכס">
                        <IconButton
                          size="small"
                          color="primary"
                          href={videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <PlayCircleOutline sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {originalUrl && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                        href={originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        מודעה מקורית
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </>
          )}

          {/* location details + realtor note (description moved up under the stat tiles) */}
          {(locationDetails || realtorNote) && <Divider sx={{ borderStyle: "dashed" }} />}
          {locationDetails && (
            <Typography variant="caption" color="text.secondary" dir="auto">
              📍 {locationDetails}
            </Typography>
          )}
          {realtorNote && (
            <Typography variant="caption" color="text.secondary" dir="auto">
              📝 הערה שלי: {realtorNote}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      {actions && <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>{actions}</DialogActions>}
    </Dialog>
  );
}
